/**
 * FP3-029C — Tiny pill that renders on a covering provider's caseload
 * row to clearly distinguish a temporary-coverage student from a
 * permanent caseload student.
 *
 * Driven entirely by `student.coverage_context` populated by
 * `GET /api/students?provider_id=X` when the row arrives via the
 * coverage UNION rather than the explicit `assigned_providers` link.
 * On the ORIGINAL primary's caseload the field is absent so the
 * pill never appears there — only on the covering provider's view.
 *
 * Variants:
 *   - "card": full chip with end date + supervising provider in the
 *     hover title (used in the Card grid)
 *   - "row":  compact pill for the List view; hover title carries
 *     the detail
 */
import { Clock } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function CoverageBadge({ student, variant = "card" }) {
  const ctx = student?.coverage_context;
  if (!ctx) return null;
  const orig = ctx.original_provider_name || "another provider";
  const endsOn = ctx.ends_on || "";
  const startsOn = ctx.starts_on || "";
  const endLabel = formatDate(endsOn);
  const title = [
    `Temporary coverage`,
    orig ? `Covering for ${orig}` : null,
    startsOn && endsOn ? `${startsOn} → ${endsOn}` : null,
    ctx.reason ? `Reason: ${ctx.reason}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  // Calm amber palette — distinct from the green services chips and
  // the gray status chips, so the covering provider can spot it at
  // a glance.
  const baseClasses =
    "inline-flex items-center gap-1 rounded-full font-medium border";
  const palette =
    "bg-[#FAF1DD] text-[#7A5B1F] border-[#E9D9A9]";

  if (variant === "row") {
    return (
      <span
        data-testid={`student-${student.id}-coverage-badge`}
        title={title}
        className={`${baseClasses} ${palette} text-[10px] px-1.5 py-0.5`}
      >
        <Clock size={9} weight="duotone" />
        Coverage
        {endLabel ? (
          <span className="opacity-80 ml-0.5">ends {endLabel}</span>
        ) : null}
      </span>
    );
  }
  return (
    <span
      data-testid={`student-${student.id}-coverage-badge`}
      title={title}
      className={`${baseClasses} ${palette} text-[10.5px] px-2 py-0.5`}
    >
      <Clock size={10} weight="duotone" />
      Temporary coverage
      {endLabel ? <span className="opacity-80">· ends {endLabel}</span> : null}
      {ctx.original_provider_name ? (
        <span className="opacity-80">· for {ctx.original_provider_name}</span>
      ) : null}
    </span>
  );
}
